/**
 * Dashboard result utilities.
 *
 * Extracts and normalizes values from API responses.
 */

/**
 * Extract a numeric count from common Helix API response shapes.
 *
 * Handles:
 * - { meta: { totalresults: N } }
 * - { _metaonly: { total: N } }
 * - { value: N }  (aggregate/cardinality response)
 *
 * @param {any} response
 * @returns {number | null}
 */
export function extractCount(response) {
  if (response === null || response === undefined) return null;
  if (response.meta?.totalresults !== undefined) return Number(response.meta.totalresults);
  if (response._metaonly?.total !== undefined) return Number(response._metaonly.total);
  // { value: N } is the Elasticsearch aggregate/cardinality shape — only reached
  // when neither meta nor _metaonly is present, so numeric config payloads are safe.
  if (typeof response.value === 'number') return response.value;
  return null;
}

/**
 * Normalize histogram bucket responses into a consistent shape.
 *
 * Handles:
 * - { buckets: [{ key, count }, ...] }
 * - { data: [{ key, count }, ...] }
 *
 * @param {any} response
 * @returns {{ key: string, value: number, label: string }[]}
 */
export function normalizeHistogram(response) {
  const buckets = response?.buckets || response?.data || response?.aggregations?.buckets || [];

  return buckets.map((b) => ({
    key: b.key ?? b.date ?? '',
    value: typeof b.count === 'number' ? b.count : typeof b.doc_count === 'number' ? b.doc_count : 0,
    label: b.label ?? b.key ?? '',
  }));
}

/**
 * Format milliseconds as a human-readable duration string.
 *
 * @param {number} ms
 * @returns {string}
 */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || isNaN(ms)) return '—';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = (ms / 86400000).toFixed(1);
  return `${days}d`;
}

/**
 * Compute trend information from current and previous values.
 *
 * @param {number | null} current
 * @param {number | null} previous
 * @param {{ lowerIsBetter?: boolean }} options
 * @returns {{ percent: number | null, direction: 'up' | 'down' | 'neutral', label: string } | null}
 */
export function computeTrend(current, previous, options = {}) {
  if (current === null || previous === null) return null;

  if (previous === 0) {
    if (current === 0) {
      return { percent: 0, direction: 'neutral', label: '—' };
    }
    // No previous baseline — percentage is meaningless; show neutral dash
    return { percent: null, direction: 'neutral', label: '—' };
  }

  const percent = ((current - previous) / previous) * 100;
  const rounded = Math.round(percent * 10) / 10;

  let direction;
  if (options.lowerIsBetter) {
    direction = percent < 0 ? 'up' : percent > 0 ? 'down' : 'neutral';
  } else {
    direction = percent > 0 ? 'up' : percent < 0 ? 'down' : 'neutral';
  }

  const sign = percent > 0 ? '+' : '';
  const label = `${sign}${rounded}%`;

  return { percent: rounded, direction, label };
}

/**
 * Format a numeric value for display, with locale-aware thousands separators.
 *
 * @param {number | null} value
 * @param {{ formatAs?: 'number' | 'duration' }} options
 * @returns {string}
 */
export function formatValue(value, options = {}) {
  if (value === null || value === undefined) return '—';
  if (options.formatAs === 'duration') return formatDuration(value);
  return value.toLocaleString();
}
